import React from "react";
import { Box, Column, Columns, Stack, Text } from "@origin-digital/ods-core";
import { Skeleton } from "@origin-digital/ods-lab";

const SkeletonIcon = ({ size }: { size: number }) => (
  <Skeleton
    variant="rectangular"
    width={size}
    height={size}
    backgroundColor="light"
  />
);

const SkeletonWideHeader = () => (
  <Box
    display="flex"
    flexDirection="row"
    justifyContent="space-between"
    alignItems="center"
    marginBottom="medium"
  >
    <Skeleton variant="text" width="50%" backgroundColor="light">
      <Text>Category heading</Text>
    </Skeleton>
    <SkeletonIcon size={32} />
  </Box>
);

const SkeletonNarrowSubLinks = () => (
  <Box marginTop="small">
    <Skeleton variant="text" width="65%" backgroundColor="light">
      <Text>Sub-link</Text>
    </Skeleton>
    <Skeleton variant="text" width="60%" backgroundColor="light">
      <Text>Sub-link</Text>
    </Skeleton>
    <Skeleton variant="text" width="50%" backgroundColor="light">
      <Text>Sub-link</Text>
    </Skeleton>
  </Box>
);

// Popular category card — large square icon, title, sub-links (top 3-column section)
const PopularSkeletonCard = ({ dataId }: { dataId: string }) => (
  <Box
    data-id={dataId}
    backgroundColor="cardBackgroundSecondary"
    borderRadius="medium"
    padding="large"
    height="full"
  >
    <Box marginBottom="medium">
      <SkeletonIcon size={80} />
    </Box>
    <Box marginBottom="small">
      <Skeleton variant="text" width="70%" backgroundColor="light">
        <Text>Category title</Text>
      </Skeleton>
      <Skeleton variant="text" width="50%" backgroundColor="light">
        <Text>Category title</Text>
      </Skeleton>
    </Box>
    <Skeleton variant="text" width="90%" backgroundColor="light">
      <Text>Sub-link placeholder</Text>
    </Skeleton>
    <Skeleton variant="text" width="80%" backgroundColor="light">
      <Text>Sub-link placeholder</Text>
    </Skeleton>
  </Box>
);

// Other category card — wide variant (Variant 2, 2-column row)
// Horizontal: heading text left, small square icon right, then sub-links below
const OtherSkeletonCardWide = ({ dataId }: { dataId: string }) => (
  <Box
    data-id={dataId}
    backgroundColor="cardBackgroundSecondary"
    borderRadius="medium"
    padding="large"
    height="full"
  >
    <SkeletonWideHeader />
    <Skeleton variant="text" width="80%" backgroundColor="light">
      <Text>Description line</Text>
    </Skeleton>
    <Skeleton variant="text" width="65%" backgroundColor="light">
      <Text>Description line</Text>
    </Skeleton>
    <Box marginTop="medium">
      <Skeleton variant="text" width="50%" backgroundColor="light">
        <Text>Sub-link</Text>
      </Skeleton>
    </Box>
    <Box marginTop="small">
      <Skeleton variant="text" width="45%" backgroundColor="light">
        <Text>Sub-link</Text>
      </Skeleton>
    </Box>
  </Box>
);

// Other category card — narrow variant (Variant 1, 3-column row)
// Medium square icon, title, description, sub-links
const OtherSkeletonCardNarrow = ({ dataId }: { dataId: string }) => (
  <Box
    data-id={dataId}
    backgroundColor="cardBackgroundSecondary"
    borderRadius="medium"
    padding="large"
    height="full"
  >
    <Box marginBottom="medium">
      <SkeletonIcon size={60} />
    </Box>
    <Box marginBottom="small">
      <Skeleton variant="text" width="70%" backgroundColor="light">
        <Text>Category title</Text>
      </Skeleton>
      <Skeleton variant="text" width="55%" backgroundColor="light">
        <Text>Category title</Text>
      </Skeleton>
    </Box>
    <Skeleton variant="text" width="90%" backgroundColor="light">
      <Text>Description</Text>
    </Skeleton>
    <Skeleton variant="text" width="80%" backgroundColor="light">
      <Text>Description</Text>
    </Skeleton>
    <SkeletonNarrowSubLinks />
  </Box>
);

export const PopularCategoriesSkeleton = () => (
  <Box
    data-id="popular-categories"
    marginX={{ lg: "none", xs: "medium", sm: "medium" }}
  >
    <Stack space="large">
      {/* Popular: 3-column grid */}
      <Columns space="large" collapseBelow="md" alignY="stretch">
        <Column>
          <PopularSkeletonCard dataId="popular-categories-skeleton-1" />
        </Column>
        <Column>
          <PopularSkeletonCard dataId="popular-categories-skeleton-2" />
        </Column>
        <Column>
          <PopularSkeletonCard dataId="popular-categories-skeleton-3" />
        </Column>
      </Columns>

      {/* Other categories — wide row: 2-column */}
      <Columns space="large" collapseBelow="md" alignY="stretch">
        <Column>
          <OtherSkeletonCardWide dataId="other-categories-skeleton-wide-1" />
        </Column>
        <Column>
          <OtherSkeletonCardWide dataId="other-categories-skeleton-wide-2" />
        </Column>
      </Columns>

      {/* Other categories — narrow row: 3-column */}
      <Columns space="large" collapseBelow="md" alignY="stretch">
        <Column>
          <OtherSkeletonCardNarrow dataId="other-categories-skeleton-narrow-1" />
        </Column>
        <Column>
          <OtherSkeletonCardNarrow dataId="other-categories-skeleton-narrow-2" />
        </Column>
        <Column>
          <OtherSkeletonCardNarrow dataId="other-categories-skeleton-narrow-3" />
        </Column>
      </Columns>
    </Stack>
  </Box>
);
